import { useState, useEffect } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Skeleton } from '../components/ui/skeleton';
import { Database, Upload, Edit, Save, FileSpreadsheet, Loader2, Download, CheckCircle, Settings, Table, ArrowRight } from 'lucide-react';
import UploadProgress from '../components/UploadProgress';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MasterData = () => {
  const { getAuthHeaders } = useAuth();
  const [bufferStock, setBufferStock] = useState([]);
  const [defaultSales, setDefaultSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState({ buffer: false, sales: false });
  const [uploadProgress, setUploadProgress] = useState({ buffer: 0, sales: 0 });
  const [editingItem, setEditingItem] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [bufferFile, setBufferFile] = useState(null);
  const [salesFile, setSalesFile] = useState(null);
  
  // Column mapping state for buffer stock
  const [showBufferMappingDialog, setShowBufferMappingDialog] = useState(false);
  const [bufferPreviewData, setBufferPreviewData] = useState(null);
  const [loadingBufferPreview, setLoadingBufferPreview] = useState(false);
  const [bufferColumnMapping, setBufferColumnMapping] = useState({
    headerLine: 1,
    itemNameColumn: 1,
    bufferLevelColumn: 2,
    skuColumn: 0  // 0 means not used
  });
  
  // Column mapping state for default sales
  const [showSalesMappingDialog, setShowSalesMappingDialog] = useState(false);
  const [salesPreviewData, setSalesPreviewData] = useState(null);
  const [loadingSalesPreview, setLoadingSalesPreview] = useState(false);
  const [salesColumnMapping, setSalesColumnMapping] = useState({
    headerLine: 1,
    itemNameColumn: 1,
    defaultSaleColumn: 2,
    skuColumn: 0  // 0 means not used
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [bufferRes, salesRes] = await Promise.all([
        axios.get(`${API}/master/buffer`, { headers: getAuthHeaders() }),
        axios.get(`${API}/master/default-sales`, { headers: getAuthHeaders() })
      ]);
      setBufferStock(bufferRes.data);
      setDefaultSales(salesRes.data);
    } catch (error) {
      console.error('Error fetching master data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Buffer Stock file handling
  const handleBufferFileSelect = async (file) => {
    if (!file) return;
    setBufferFile(file);
    
    setLoadingBufferPreview(true);
    setShowBufferMappingDialog(true);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/master/buffer/preview`, formData, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      setBufferPreviewData(response.data);
      setBufferColumnMapping({
        headerLine: 1,
        itemNameColumn: 1,
        bufferLevelColumn: 2,
        skuColumn: 0
      });
    } catch (error) {
      toast.error('Failed to preview file');
      setShowBufferMappingDialog(false);
    } finally {
      setLoadingBufferPreview(false);
    }
  };

  const handleUploadBuffer = async () => {
    if (!bufferFile) return;

    setUploading(prev => ({ ...prev, buffer: true }));
    setUploadProgress(prev => ({ ...prev, buffer: 0 }));
    setShowBufferMappingDialog(false);
    
    const formData = new FormData();
    formData.append('file', bufferFile);

    try {
      const params = new URLSearchParams({
        header_line: bufferColumnMapping.headerLine,
        item_name_column: bufferColumnMapping.itemNameColumn,
        buffer_level_column: bufferColumnMapping.bufferLevelColumn,
        sku_column: bufferColumnMapping.skuColumn
      });
      
      const response = await axios.post(`${API}/master/buffer/upload?${params.toString()}`, formData, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(prev => ({ ...prev, buffer: percentCompleted }));
        }
      });
      
      const { items_processed, items_skipped } = response.data;
      if (items_skipped > 0) {
        toast.warning(`${items_processed} items uploaded, ${items_skipped} skipped`);
      } else {
        toast.success(response.data.message);
      }
      
      setBufferFile(null);
      setBufferPreviewData(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload buffer stock file');
    } finally {
      setUploading(prev => ({ ...prev, buffer: false }));
      setUploadProgress(prev => ({ ...prev, buffer: 0 }));
    }
  };

  // Default Sales file handling
  const handleSalesFileSelect = async (file) => {
    if (!file) return;
    setSalesFile(file);
    
    setLoadingSalesPreview(true);
    setShowSalesMappingDialog(true);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/master/default-sales/preview`, formData, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      setSalesPreviewData(response.data);
      setSalesColumnMapping({
        headerLine: 1,
        itemNameColumn: 1,
        defaultSaleColumn: 2,
        skuColumn: 0
      });
    } catch (error) {
      toast.error('Failed to preview file');
      setShowSalesMappingDialog(false);
    } finally {
      setLoadingSalesPreview(false);
    }
  };

  const handleUploadSales = async () => {
    if (!salesFile) return;

    setUploading(prev => ({ ...prev, sales: true }));
    setUploadProgress(prev => ({ ...prev, sales: 0 }));
    setShowSalesMappingDialog(false);
    
    const formData = new FormData();
    formData.append('file', salesFile);

    try {
      const params = new URLSearchParams({
        header_line: salesColumnMapping.headerLine,
        item_name_column: salesColumnMapping.itemNameColumn,
        default_sale_column: salesColumnMapping.defaultSaleColumn,
        sku_column: salesColumnMapping.skuColumn
      });
      
      const response = await axios.post(`${API}/master/default-sales/upload?${params.toString()}`, formData, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(prev => ({ ...prev, sales: percentCompleted }));
        }
      });
      
      const { items_processed, items_skipped } = response.data;
      if (items_skipped > 0) {
        toast.warning(`${items_processed} items uploaded, ${items_skipped} skipped`);
      } else {
        toast.success(response.data.message);
      }
      
      setSalesFile(null);
      setSalesPreviewData(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload default sales file');
    } finally {
      setUploading(prev => ({ ...prev, sales: false }));
      setUploadProgress(prev => ({ ...prev, sales: 0 }));
    }
  };

  const handleEditBuffer = async (item_name) => {
    try {
      await axios.put(`${API}/master/buffer/by-name/${encodeURIComponent(item_name)}`, {
        sku: editingItem.sku,
        item_name: item_name,
        buffer_level: parseInt(editValue)
      }, { headers: getAuthHeaders() });
      toast.success('Buffer stock updated');
      setEditingItem(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update buffer stock');
    }
  };

  const handleEditSales = async (item_name) => {
    try {
      await axios.put(`${API}/master/default-sales/by-name/${encodeURIComponent(item_name)}`, {
        sku: editingItem.sku,
        item_name: item_name,
        default_sale: parseFloat(editValue)
      }, { headers: getAuthHeaders() });
      toast.success('Default sale updated');
      setEditingItem(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update default sale');
    }
  };

  const downloadTemplate = (type) => {
    let data, filename;
    if (type === 'buffer') {
      data = [
        ['Item Name', 'Buffer Level'],
        ['1 Way Switch', 100],
        ['2 Way Switch', 80],
        ['Socket', 120]
      ];
      filename = 'buffer_stock_template.xlsx';
    } else {
      data = [
        ['Item Name', 'Default Sale'],
        ['1 Way Switch', 50],
        ['2 Way Switch', 40],
        ['Socket', 60]
      ];
      filename = 'default_sales_template.xlsx';
    }
    
    const ws = XLSX.utils.aoa_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Template');
    XLSX.writeFile(wb, filename);
  };

  const getBufferMappedPreview = () => {
    if (!bufferPreviewData) return [];
    const dataStartLine = bufferColumnMapping.headerLine + 1;
    return bufferPreviewData.preview_rows
      .filter(row => row.line >= dataStartLine)
      .slice(0, 5)
      .map(row => ({
        itemName: row.data[bufferColumnMapping.itemNameColumn - 1] || '',
        bufferLevel: row.data[bufferColumnMapping.bufferLevelColumn - 1] || '',
        sku: bufferColumnMapping.skuColumn > 0 ? (row.data[bufferColumnMapping.skuColumn - 1] || '') : ''
      }));
  };

  const getSalesMappedPreview = () => {
    if (!salesPreviewData) return [];
    const dataStartLine = salesColumnMapping.headerLine + 1;
    return salesPreviewData.preview_rows
      .filter(row => row.line >= dataStartLine)
      .slice(0, 5)
      .map(row => ({
        itemName: row.data[salesColumnMapping.itemNameColumn - 1] || '',
        defaultSale: row.data[salesColumnMapping.defaultSaleColumn - 1] || '',
        sku: salesColumnMapping.skuColumn > 0 ? (row.data[salesColumnMapping.skuColumn - 1] || '') : ''
      }));
  };

  // Reusable Column Mapping Dialog Component
  const ColumnMappingDialog = ({ 
    open, 
    onOpenChange, 
    title,
    loading: dialogLoading, 
    previewData, 
    columnMapping, 
    setColumnMapping, 
    onUpload, 
    uploading: dialogUploading,
    valueColumnLabel,
    valueColumnKey
  }) => (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            {title}
          </DialogTitle>
        </DialogHeader>
        
        {dialogLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            <span className="ml-3 text-slate-500">Loading file preview...</span>
          </div>
        ) : previewData && (
          <div className="space-y-6">
            {/* File Info */}
            <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
              <FileSpreadsheet className="w-8 h-8 text-blue-500" />
              <div>
                <p className="font-medium">{previewData.filename}</p>
                <p className="text-sm text-slate-500">
                  {previewData.total_rows} rows × {previewData.total_columns} columns
                </p>
              </div>
            </div>

            {/* Mapping Configuration */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Header Line</Label>
                <Select 
                  value={columnMapping.headerLine.toString()} 
                  onValueChange={(v) => setColumnMapping(prev => ({ ...prev, headerLine: parseInt(v) }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: Math.min(previewData.preview_rows.length, 15) }, (_, i) => (
                      <SelectItem key={i + 1} value={(i + 1).toString()}>Line {i + 1}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-400">Data starts from line {columnMapping.headerLine + 1}</p>
              </div>
              
              <div className="space-y-2">
                <Label>Item Name Column <span className="text-red-500">*</span></Label>
                <Select 
                  value={columnMapping.itemNameColumn.toString()} 
                  onValueChange={(v) => setColumnMapping(prev => ({ ...prev, itemNameColumn: parseInt(v) }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {previewData.column_letters.map((letter, idx) => (
                      <SelectItem key={idx + 1} value={(idx + 1).toString()}>
                        Column {letter} ({idx + 1})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>{valueColumnLabel} Column <span className="text-red-500">*</span></Label>
                <Select 
                  value={columnMapping[valueColumnKey].toString()} 
                  onValueChange={(v) => setColumnMapping(prev => ({ ...prev, [valueColumnKey]: parseInt(v) }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {previewData.column_letters.map((letter, idx) => (
                      <SelectItem key={idx + 1} value={(idx + 1).toString()}>
                        Column {letter} ({idx + 1})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>SKU Column <span className="text-slate-400 text-xs">(Optional)</span></Label>
                <Select 
                  value={columnMapping.skuColumn.toString()} 
                  onValueChange={(v) => setColumnMapping(prev => ({ ...prev, skuColumn: parseInt(v) }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Not Used</SelectItem>
                    {previewData.column_letters.map((letter, idx) => (
                      <SelectItem key={idx + 1} value={(idx + 1).toString()}>
                        Column {letter} ({idx + 1})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Raw Preview */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Table className="w-4 h-4" />
                <Label>File Preview (First 15 rows)</Label>
              </div>
              <div className="border rounded-lg overflow-x-auto max-h-48">
                <table className="w-full text-xs">
                  <thead className="bg-slate-100 sticky top-0">
                    <tr>
                      <th className="px-2 py-1.5 text-left font-medium text-slate-600 w-16">Line</th>
                      {previewData.column_letters.map((letter, idx) => (
                        <th key={letter} className={`px-2 py-1.5 text-left font-medium min-w-[100px] ${
                          idx + 1 === columnMapping.itemNameColumn ? 'bg-green-100 text-green-700' :
                          idx + 1 === columnMapping[valueColumnKey] ? 'bg-purple-100 text-purple-700' :
                          idx + 1 === columnMapping.skuColumn && columnMapping.skuColumn > 0 ? 'bg-blue-100 text-blue-700' :
                          'text-slate-600'
                        }`}>
                          {letter}
                          {idx + 1 === columnMapping.itemNameColumn && <span className="ml-1">(Name)</span>}
                          {idx + 1 === columnMapping[valueColumnKey] && <span className="ml-1">({valueColumnLabel})</span>}
                          {idx + 1 === columnMapping.skuColumn && columnMapping.skuColumn > 0 && <span className="ml-1">(SKU)</span>}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.preview_rows.slice(0, 15).map((row) => (
                      <tr 
                        key={row.line} 
                        className={`border-t ${
                          row.line === columnMapping.headerLine ? 'bg-yellow-50 font-medium' : 
                          row.line > columnMapping.headerLine ? 'bg-white' : 'bg-slate-50 text-slate-400'
                        }`}
                      >
                        <td className="px-2 py-1.5 font-mono">
                          {row.line}
                          {row.line === columnMapping.headerLine && (
                            <Badge variant="secondary" className="ml-1 text-[10px] py-0">Header</Badge>
                          )}
                        </td>
                        {row.data.map((cell, idx) => (
                          <td key={idx} className={`px-2 py-1.5 truncate max-w-[150px] ${
                            idx + 1 === columnMapping.itemNameColumn ? 'bg-green-50' :
                            idx + 1 === columnMapping[valueColumnKey] ? 'bg-purple-50' :
                            idx + 1 === columnMapping.skuColumn && columnMapping.skuColumn > 0 ? 'bg-blue-50' : ''
                          }`}>
                            {cell || <span className="text-slate-300">—</span>}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onUpload} disabled={dialogUploading}>
            {dialogUploading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Upload with Mapping
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return (
    <div className="space-y-6" data-testid="master-data-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Master Data
        </h1>
        <p className="text-slate-500 mt-1">Manage buffer stock levels and default sales values (Company Level)</p>
      </div>

      <Tabs defaultValue="buffer" className="space-y-6">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="buffer" data-testid="buffer-tab">Buffer Stock</TabsTrigger>
          <TabsTrigger value="sales" data-testid="default-sales-tab">Default Sales</TabsTrigger>
        </TabsList>

        {/* Buffer Stock Tab */}
        <TabsContent value="buffer" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <Database className="w-5 h-5 text-blue-500" />
                Upload Buffer Stock Master
              </CardTitle>
              <CardDescription>Define minimum buffer levels for each item (applies to all depots)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div
                className="upload-zone cursor-pointer"
                onClick={() => document.getElementById('buffer-file-input').click()}
                data-testid="buffer-dropzone"
              >
                <input
                  id="buffer-file-input"
                  type="file"
                  accept=".xlsx,.xls"
                  className="hidden"
                  onChange={(e) => handleBufferFileSelect(e.target.files[0])}
                />
                {bufferFile ? (
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-8 h-8 text-green-500" />
                    <div>
                      <p className="font-medium text-slate-900">{bufferFile.name}</p>
                      <p className="text-sm text-slate-500">{(bufferFile.size / 1024).toFixed(1)} KB</p>
                    </div>
                  </div>
                ) : (
                  <>
                    <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                    <p className="text-slate-600 font-medium">Drop your Excel file here</p>
                    <p className="text-slate-400 text-sm mt-1">or click to browse</p>
                  </>
                )}
              </div>
              
              {bufferFile && (
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => handleBufferFileSelect(bufferFile)}
                  data-testid="configure-buffer-mapping-btn"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Configure Column Mapping
                </Button>
              )}
              
              {uploading.buffer ? (
                <UploadProgress progress={uploadProgress.buffer} message="Uploading buffer stock..." />
              ) : (
                <div className="flex gap-2">
                  <Button
                    onClick={() => bufferFile && bufferPreviewData ? handleUploadBuffer() : null}
                    disabled={!bufferFile || uploading.buffer}
                    className="flex-1"
                    data-testid="upload-buffer-btn"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Buffer Stock
                  </Button>
                  <Button variant="outline" onClick={() => downloadTemplate('buffer')}>
                    <Download className="w-4 h-4 mr-2" />
                    Template
                  </Button>
                </div>
              )}
              
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                <p className="text-xs text-blue-700 font-medium mb-1">Company-Level Buffer Stock</p>
                <p className="text-xs text-blue-600">Buffer levels set here apply to all depots. When stock falls below the buffer level at any depot, it will trigger a reorder alert.</p>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="buffer-stock-table">
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                Buffer Stock Levels
                <Badge variant="secondary" className="ml-2">{bufferStock.length} items</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-6 space-y-4">
                  {[1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
                </div>
              ) : bufferStock.length === 0 ? (
                <div className="text-center py-12">
                  <Database className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No buffer stock data. Upload a master file to get started.</p>
                </div>
              ) : (
                <div className="overflow-x-auto max-h-[400px]">
                  <table className="data-table">
                    <thead className="sticky top-0 bg-slate-50 z-10">
                      <tr>
                        <th>SKU</th>
                        <th>Item Name</th>
                        <th className="text-right">Buffer Level</th>
                        <th className="w-24">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bufferStock.map((item) => (
                        <tr key={item.item_name}>
                          <td className="font-mono text-sm">{item.sku}</td>
                          <td>{item.item_name}</td>
                          <td className="numeric font-bold">{item.buffer_level.toLocaleString()}</td>
                          <td>
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => { setEditingItem(item); setEditValue(item.buffer_level.toString()); }}
                                  data-testid={`edit-buffer-${item.sku}`}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                              </DialogTrigger>
                              <DialogContent>
                                <DialogHeader>
                                  <DialogTitle>Edit Buffer Stock</DialogTitle>
                                </DialogHeader>
                                <div className="space-y-4 py-4">
                                  <div className="space-y-2">
                                    <Label>SKU</Label>
                                    <Input value={item.sku} disabled />
                                  </div>
                                  <div className="space-y-2">
                                    <Label>Buffer Level</Label>
                                    <Input
                                      type="number"
                                      value={editValue}
                                      onChange={(e) => setEditValue(e.target.value)}
                                      data-testid="edit-buffer-input"
                                    />
                                  </div>
                                </div>
                                <DialogFooter>
                                  <Button onClick={() => handleEditBuffer(item.item_name)} data-testid="save-buffer-btn">
                                    <Save className="w-4 h-4 mr-2" />
                                    Save
                                  </Button>
                                </DialogFooter>
                              </DialogContent>
                            </Dialog>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Default Sales Tab */}
        <TabsContent value="sales" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <Database className="w-5 h-5 text-green-500" />
                Upload Default Sales Master
              </CardTitle>
              <CardDescription>Define default sales values for cold start items (used when less than 3 months of data)</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div
                className="upload-zone cursor-pointer"
                onClick={() => document.getElementById('sales-file-input').click()}
                data-testid="sales-dropzone"
              >
                <input
                  id="sales-file-input"
                  type="file"
                  accept=".xlsx,.xls"
                  className="hidden"
                  onChange={(e) => handleSalesFileSelect(e.target.files[0])}
                />
                {salesFile ? (
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-8 h-8 text-green-500" />
                    <div>
                      <p className="font-medium text-slate-900">{salesFile.name}</p>
                      <p className="text-sm text-slate-500">{(salesFile.size / 1024).toFixed(1)} KB</p>
                    </div>
                  </div>
                ) : (
                  <>
                    <Upload className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                    <p className="text-slate-600 font-medium">Drop your Excel file here</p>
                    <p className="text-slate-400 text-sm mt-1">or click to browse</p>
                  </>
                )}
              </div>
              
              {salesFile && (
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => handleSalesFileSelect(salesFile)}
                  data-testid="configure-sales-mapping-btn"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Configure Column Mapping
                </Button>
              )}
              
              {uploading.sales ? (
                <UploadProgress progress={uploadProgress.sales} message="Uploading default sales..." />
              ) : (
                <div className="flex gap-2">
                  <Button
                    onClick={() => salesFile && salesPreviewData ? handleUploadSales() : null}
                    disabled={!salesFile || uploading.sales}
                    className="flex-1"
                    data-testid="upload-default-sales-btn"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Default Sales
                  </Button>
                  <Button variant="outline" onClick={() => downloadTemplate('sales')}>
                    <Download className="w-4 h-4 mr-2" />
                    Template
                  </Button>
                </div>
              )}
              
              <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                <p className="text-xs text-green-700 font-medium mb-1">Cold Start Logic</p>
                <p className="text-xs text-green-600">Default sales values are used in the moving average calculation when an item has less than 3 months of sales history.</p>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="default-sales-table">
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                Default Sales Values
                <Badge variant="secondary" className="ml-2">{defaultSales.length} items</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-6 space-y-4">
                  {[1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
                </div>
              ) : defaultSales.length === 0 ? (
                <div className="text-center py-12">
                  <Database className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No default sales data. Upload a master file to get started.</p>
                </div>
              ) : (
                <div className="overflow-x-auto max-h-[400px]">
                  <table className="data-table">
                    <thead className="sticky top-0 bg-slate-50 z-10">
                      <tr>
                        <th>SKU</th>
                        <th>Item Name</th>
                        <th className="text-right">Default Sale Value</th>
                        <th className="w-24">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {defaultSales.map((item) => (
                        <tr key={item.item_name}>
                          <td className="font-mono text-sm">{item.sku}</td>
                          <td>{item.item_name}</td>
                          <td className="numeric font-bold">{item.default_sale.toLocaleString()}</td>
                          <td>
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => { setEditingItem(item); setEditValue(item.default_sale.toString()); }}
                                  data-testid={`edit-sales-${item.sku}`}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                              </DialogTrigger>
                              <DialogContent>
                                <DialogHeader>
                                  <DialogTitle>Edit Default Sale</DialogTitle>
                                </DialogHeader>
                                <div className="space-y-4 py-4">
                                  <div className="space-y-2">
                                    <Label>SKU</Label>
                                    <Input value={item.sku} disabled />
                                  </div>
                                  <div className="space-y-2">
                                    <Label>Default Sale Value</Label>
                                    <Input
                                      type="number"
                                      step="0.01"
                                      value={editValue}
                                      onChange={(e) => setEditValue(e.target.value)}
                                      data-testid="edit-sales-input"
                                    />
                                  </div>
                                </div>
                                <DialogFooter>
                                  <Button onClick={() => handleEditSales(item.item_name)} data-testid="save-sales-btn">
                                    <Save className="w-4 h-4 mr-2" />
                                    Save
                                  </Button>
                                </DialogFooter>
                              </DialogContent>
                            </Dialog>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Buffer Stock Column Mapping Dialog */}
      <ColumnMappingDialog
        open={showBufferMappingDialog}
        onOpenChange={setShowBufferMappingDialog}
        title="Configure Buffer Stock Column Mapping"
        loading={loadingBufferPreview}
        previewData={bufferPreviewData}
        columnMapping={bufferColumnMapping}
        setColumnMapping={setBufferColumnMapping}
        onUpload={handleUploadBuffer}
        uploading={uploading.buffer}
        valueColumnLabel="Buffer"
        valueColumnKey="bufferLevelColumn"
      />

      {/* Default Sales Column Mapping Dialog */}
      <ColumnMappingDialog
        open={showSalesMappingDialog}
        onOpenChange={setShowSalesMappingDialog}
        title="Configure Default Sales Column Mapping"
        loading={loadingSalesPreview}
        previewData={salesPreviewData}
        columnMapping={salesColumnMapping}
        setColumnMapping={setSalesColumnMapping}
        onUpload={handleUploadSales}
        uploading={uploading.sales}
        valueColumnLabel="Default Sale"
        valueColumnKey="defaultSaleColumn"
      />
    </div>
  );
};

export default MasterData;
